/** @format */

import {Box, Grid, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import Alert from '@mui/material/Alert';

import Modal from 'components/Modal';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import SecondaryMediumButton from 'components/buttons/SecondaryMediumButton';
import {useBackgroundPath, useSearchParams} from 'helpers/routerHelpers';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {OauthProvider} from 'graphql/generated/types';

import {useDisconnectProviderMutation} from '../graphql/__generated__';

const PREFIX = 'ConfirmDisconnect';

const classes = {
  body: `${PREFIX}-body`,
};

const StyledModal = styled(Modal)(({theme}) => ({
  [`& .${classes.body}`]: {
    color: theme.palette.text.secondary,
  },
}));

const ConfirmDisconnect = () => {
  const navigate = useNavigate();

  const searchParams = useSearchParams();
  const companyId = searchParams.get('companyId');
  const provider = searchParams.get('provider') as OauthProvider | null;

  const backgroundPath = useBackgroundPath();

  const {mixpanel} = useMixpanelContext();

  const {t} = useTranslation();

  const [disconnectProvider, {loading}] = useDisconnectProviderMutation();

  if (!companyId) {
    return (
      <Alert severity='error'>
        <Trans>Кампания не найдена</Trans>
      </Alert>
    );
  }

  if (!provider) {
    return (
      <Alert severity='error'>
        <Trans>Провайдер не найден</Trans>
      </Alert>
    );
  }

  const renderButtons = () => {
    const handleConfirm = async () => {
      const lcProvider = provider.toLowerCase();
      mixpanel.track(`click_confirm_disconnect_${lcProvider}_button`);

      await disconnectProvider({
        variables: {provider, companyId},
      });

      navigate(backgroundPath, {replace: true});
    };

    const handleCancel = () => {
      const lcProvider = provider.toLowerCase();
      mixpanel.track(`click_cancel_disconnect_${lcProvider}_button`);

      navigate(-1);
    };

    return (
      <Box
        sx={{
          mt: 16,
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <PrimaryMediumButton disabled={loading} fullWidth onClick={handleConfirm}>
              <Trans>Отключить</Trans>
            </PrimaryMediumButton>
          </Grid>

          <Grid item xs={12} sm={5}>
            <SecondaryMediumButton disabled={loading} onClick={handleCancel}>
              <Trans>Оставить</Trans>
            </SecondaryMediumButton>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <StyledModal
      header={t(
        'Вы уверены, что хотите отключить кабинет?',
        'Вы уверены, что хотите отключить кабинет?',
      )}
      showCloseIcon={false}
    >
      <Typography className={classes.body} variant='body1'>
        <Trans>
          Кабинет удалится и, чтобы подключить его снова, нужно будет заново вводить все данные
          авторизации.
        </Trans>
      </Typography>
      {renderButtons()}
    </StyledModal>
  );
};

export default ConfirmDisconnect;
